import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { DraftingPane } from '~/components/ai/DraftingPane/DraftingPane';
import { SparkleIcon } from '~/components/ai/shared/SparkleIcon/SparkleIcon';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { ConversationModel } from '~/models';
import { useAgentConversation } from '../conversation/useAgentConversation';
export const GetBulkDraftAgentPaneTID = (parentTestId)=>getTestIds(parentTestId);
// WHY: sits below the scroll-independent sticky bulk-actions bar
const PANE_TOP_PX = 192;
const SCREEN_BOTTOM_GAP_PX = 32;
const SCREEN_RIGHT_GAP_PX = 24;
const PANE_WIDTH_PX = 482;
const BULK_INTERCOM_VERTICAL_PADDING = 100;
export const BulkDraftAgentPane = (param)=>{
    let { scope, draftNames, onFilterUpdate, onSelectionUpdate, onScopeRestore, onDraftsChanged, onAssistantMessage, isOpen: controlledIsOpen, onOpenChange, testId = '' } = param;
    const { t } = useLocaleKeys();
    const { BulkDraftAgentPaneTestIds } = useMemo(()=>GetBulkDraftAgentPaneTID(testId), [
        testId
    ]);
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = controlledIsOpen != null;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (open)=>{
        if (!isControlled) setInternalIsOpen(open);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    };
    const { api: storeApi } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const conversationIdRef = useRef(null);
    const createInFlightRef = useRef(null);
    const panePosition = {
        top: 'auto',
        bottom: "".concat(SCREEN_BOTTOM_GAP_PX, "px"),
        right: "".concat(SCREEN_RIGHT_GAP_PX, "px"),
        width: "".concat(PANE_WIDTH_PX, "px"),
        height: "calc(100vh - ".concat(PANE_TOP_PX, "px - ").concat(SCREEN_BOTTOM_GAP_PX, "px)")
    };
    const api = {
        get: (conversationId)=>storeApi.agenticBulkDraft.get(conversationId),
        sendMessage: (args)=>storeApi.agenticBulkDraft.sendMessage(_object_spread_props(_object_spread({}, args), {
                scope
            })),
        undo: (args)=>storeApi.agenticBulkDraft.undo(args)
    };
    const conversation = useAgentConversation({
        api,
        ensureConversationId,
        getConversationId,
        resetConversation,
        initialConversationId: null,
        isOpen,
        enrichableTypes: [
            'bulk-applied'
        ],
        clearingLabel: t.agenticDrafting.body.clearing(),
        canClearWithoutUserMessage: true,
        resetEndsTurn: true,
        onHydrated: applyPersistedUpdates,
        onAssistantMessage: handleAssistantMessage,
        onDraftApplied: (draftId)=>onDraftsChanged === null || onDraftsChanged === void 0 ? void 0 : onDraftsChanged([
                draftId
            ]),
        onError: (reason)=>{
            if (reason === 'hydrate') showErrorToast(t.agenticDrafting.errors.hydrate());
            else if (reason === 'clear') showErrorToast(t.agenticDrafting.errors.clear());
        }
    });
    function getConversationId() {
        return conversationIdRef.current;
    }
    function resetConversation() {
        conversationIdRef.current = null;
        createInFlightRef.current = null;
    }
    function createFreshConversation() {
        return storeApi.agenticBulkDraft.create({
            matchedDraftCount: scope.matchedDraftCount,
            draftNames
        }).then((param)=>{
            let { conversationId, message } = param;
            conversationIdRef.current = conversationId;
            conversation.appendMessage(message);
            return conversationId;
        });
    }
    function ensureConversationId() {
        if (conversationIdRef.current != null) return Promise.resolve(conversationIdRef.current);
        if (createInFlightRef.current != null) return createInFlightRef.current;
        const pending = storeApi.agenticBulkDraft.recent().then((restored)=>{
            if (restored == null) return createFreshConversation();
            conversationIdRef.current = restored.conversation.conversationId;
            conversation.hydrate(restored.conversation.messages);
            if (restored.scope != null) onScopeRestore === null || onScopeRestore === void 0 ? void 0 : onScopeRestore(restored.scope);
            return restored.conversation.conversationId;
        }).finally(()=>{
            createInFlightRef.current = null;
        });
        createInFlightRef.current = pending;
        return pending;
    }
    function applyPersistedUpdates(messages) {
        const conversation = ConversationModel.ofMessages(messages);
        const filterUpdate = conversation.latestPersistedFilterUpdate();
        if (filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(filterUpdate);
        const selectionUpdate = conversation.latestPersistedSelectionUpdate();
        if (selectionUpdate != null && selectionUpdate.length > 0) onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(selectionUpdate);
    }
    function handleAssistantMessage(message) {
        const content = message.assistantContent();
        if (content == null) return;
        switch(content.type){
            case 'bulk-confirmation':
            case 'chat-text':
                if (content.filterUpdate != null) onFilterUpdate === null || onFilterUpdate === void 0 ? void 0 : onFilterUpdate(content.filterUpdate);
                if (content.selectionUpdate != null && content.selectionUpdate.length > 0) {
                    onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(content.selectionUpdate);
                }
                break;
            case 'sequenced-confirmation':
                if (content.selectionUpdate != null && content.selectionUpdate.length > 0) {
                    onSelectionUpdate === null || onSelectionUpdate === void 0 ? void 0 : onSelectionUpdate(content.selectionUpdate);
                }
                break;
            case 'bulk-applied':
                onDraftsChanged === null || onDraftsChanged === void 0 ? void 0 : onDraftsChanged(content.appliedIds);
                break;
            case 'reverted':
                onDraftsChanged === null || onDraftsChanged === void 0 ? void 0 : onDraftsChanged(content.revertedItemIds);
                break;
        }
        onAssistantMessage === null || onAssistantMessage === void 0 ? void 0 : onAssistantMessage(message);
    }
    const sendPrompt = (text, options)=>{
        const attachment = conversation.chat.pendingAttachment;
        if (attachment != null) {
            conversation.clearPendingAttachment();
            const trimmed = text.trim();
            return conversation.send(_object_spread({
                kind: 'fileUploaded',
                fileId: attachment.fileId,
                fileName: attachment.fileName
            }, trimmed.length > 0 ? {
                text: trimmed
            } : {}));
        }
        return conversation.send(_object_spread({
            kind: 'prompt',
            text
        }, (options === null || options === void 0 ? void 0 : options.intentHint) != null ? {
            intentHint: options.intentHint
        } : {}));
    };
    const dismissProposal = (messageId)=>conversation.send({
            kind: 'dismiss',
            messageId
        });
    const skipProposal = (messageId)=>conversation.send({
            kind: 'skip',
            messageId
        });
    const skipTheRestProposal = (messageId)=>conversation.send({
            kind: 'skipTheRest',
            messageId
        });
    const applyProposal = (messageId, selectedItemIds)=>conversation.send({
            kind: 'apply',
            messageId,
            selectedItemIds
        });
    const takeSuggestion = (messageId, suggestion)=>{
        var _target_assistantContent;
        const target = conversation.chat.messages.find((message)=>message.id() === messageId);
        if ((target === null || target === void 0 ? void 0 : (_target_assistantContent = target.assistantContent()) === null || _target_assistantContent === void 0 ? void 0 : _target_assistantContent.type) === 'clarifying-question') {
            return conversation.send({
                kind: 'prompt',
                text: suggestion.label
            });
        }
        return conversation.send({
            kind: 'listSelectClick',
            messageId,
            itemId: suggestion.id,
            label: suggestion.label
        });
    };
    const handlers = {
        onApply: applyProposal,
        onDismiss: dismissProposal,
        onSkip: skipProposal,
        onSkipTheRest: skipTheRestProposal,
        onListSelectClick: (messageId, itemId)=>applyProposal(messageId, [
                itemId
            ]),
        onNumberedListClick: (messageId, option)=>applyProposal(messageId, [
                option.id
            ]),
        onSubmitFeedback: ()=>undefined,
        onDismissFeedback: ()=>undefined,
        onSuggestionClick: takeSuggestion,
        onUndo: conversation.undo,
        onFollowUpPrompt: sendPrompt,
        onStarterPromptClick: sendPrompt
    };
    return /*#__PURE__*/ _jsx(DraftingPane, {
        testId: BulkDraftAgentPaneTestIds.OpenPaneHeader(),
        isOpen: isOpen,
        position: panePosition,
        dragResize: true,
        intercom: {
            collapsedVerticalPadding: BULK_INTERCOM_VERTICAL_PADDING
        },
        header: {
            mode: 'collapsible',
            icon: /*#__PURE__*/ _jsx(SparkleIcon, {}),
            title: t.agenticDrafting.header.bulkTitle(),
            onClearChat: conversation.clear,
            isClearDisabled: conversation.view.isClearDisabled,
            clearChatBiName: 'clear_bulk_agent_chat',
            onToggle: ()=>setIsOpen(!isOpen),
            onExpand: ()=>setIsOpen(true),
            toggleBiName: 'toggle_bulk_agent_pane',
            collapseChevronOrientation: 'vertical'
        },
        chat: conversation.chat,
        activeMessage: conversation.view.activeMessage,
        placeholderPool: [],
        onSubmitPrompt: sendPrompt,
        onFileUpload: conversation.onFileUpload,
        onRemoveAttachment: conversation.onRemoveAttachment,
        handlers: handlers
    });
};
